import { Fragment, useEffect } from 'react';
import { observer } from 'mobx-react';

import { Tooltip } from '@genially/design-system';
import { StatisticsIcon } from '@genially/icons';
import { PlayUserType } from '@genially/p2p-lib';
import { FEATURE_FLAGS } from '@genially/ui/build/constants/featureFlags';
import { calculate } from '@genially/utils/build/module';

import { useModal } from '../../../../bootstrap/application/components/app/components/ModalProvider/ModalProvider.context';
import { CheckIsGeniallyCompatibleGuard } from '../../../../bootstrap/application/components/app/Routes/guards/CheckIsGeniallyCompatibleGuard';
import { Header } from '../../../../bootstrap/application/components/app/Routes/layouts/CommonLayout/components/Header/Header';
import { RouteNames } from '../../../../bootstrap/domain/RouteNames';
import { useShowDebugModal } from '../../../../shared/application/components/DebugModal/hooks/useShowDebugModal';
import { Ping } from '../../../../shared/application/components/DebugPing/Ping';
import { LottieCountdown } from '../../../../shared/application/components/LottieCountdown/LottieCountdown';
import { useNavigation } from '../../../../shared/application/hooks/useNavigation';
import { useScreenWakeLockWhilePlaying } from '../../../../shared/application/hooks/useScreenWakeLockWhilePlaying';
import { useSnowplowService } from '../../../../shared/application/hooks/useSnowplow';
import { BenchmarkButton } from './components/BenchmarkButton/BenchmarkButton';
import { CheatModal } from './components/CheatModal/CheatModal';
import { useHostClientContext } from './components/HostClientProvider/hooks/useHostClientContext';
import { HostGame } from './components/HostGame/HostGame';
import { HostGameFinished } from './components/HostGameFinished/HostGameFinished';
import { HostLobby } from './components/HostLobby/HostLobby';
import { FloatingButton, FloatingButtons } from './HostGamePage.styled';

export const HostGamePage = observer(() => {
  const { client } = useHostClientContext();
  const { openModal, closeModal } = useModal();
  const { replaceUrl } = useNavigation();

  const canShowDebugButtons = client.hasFeatureFlag(
    FEATURE_FLAGS.FEAT_LT4_PLAY_BENCHMARK,
  );
  const isOpenMode = client.hasFeatureFlag(FEATURE_FLAGS.FEAT_LT4_PLAY_OPEN_MODE);
  const { showDebugModal } = useShowDebugModal(client);
  Ping.isVisible = canShowDebugButtons;

  const { sendPageView } = useSnowplowService();

  const { geniallyId, sessionId, teamId } = client;

  // Puts the session id in the URL so a reload rejoins the same session instead of creating a
  // new one and orphaning connected players. Lives here rather than in the lobby because open
  // mode never renders the lobby.
  useEffect(() => {
    replaceUrl({
      to: RouteNames.Host,
      params: {
        geniallyId,
        teamId,
        sessionId,
      },
    });
  }, [geniallyId, teamId, sessionId, replaceUrl]);

  useEffect(() => {
    if (client.sessionId) {
      sendPageView();
    }
  }, [client.sessionId, sendPageView]);

  const openBenchmarkModal = () => {
    openModal(parentElement => (
      <CheatModal client={client} parentElement={parentElement} closeModal={closeModal} />
    ));
  };

  const BenchmarkFloatingButton = calculate(() => {
    if (!canShowDebugButtons) {
      return null;
    }

    return (
      <FloatingButton onClick={openBenchmarkModal}>
        <Tooltip text="Benchmark">
          <BenchmarkButton />
        </Tooltip>
      </FloatingButton>
    );
  });

  const DebugFloatingButton = calculate(() => {
    if (!canShowDebugButtons) {
      return null;
    }

    return (
      <FloatingButton onClick={showDebugModal}>
        <Tooltip text="Debug">
          <StatisticsIcon />
        </Tooltip>
      </FloatingButton>
    );
  });

  useScreenWakeLockWhilePlaying({ client });

  const FloatingButtonsComponent = (
    <FloatingButtons>
      {BenchmarkFloatingButton}
      {DebugFloatingButton}
    </FloatingButtons>
  );

  if (client.game.isFinished) {
    return (
      <Fragment>
        {FloatingButtonsComponent}
        <HostGameFinished />
      </Fragment>
    );
  }

  if (!client.game.isStarted) {
    return (
      <Fragment>
        <Header />
        {FloatingButtonsComponent}
        <HostLobby />
      </Fragment>
    );
  }

  return (
    <Fragment>
      {!isOpenMode && <LottieCountdown show />}
      {FloatingButtonsComponent}
      <CheckIsGeniallyCompatibleGuard
        geniallyId={client.geniallyId}
        userType={PlayUserType.Host}
      >
        <HostGame />
      </CheckIsGeniallyCompatibleGuard>
    </Fragment>
  );
});
